import axios from 'axios'
import { forceUpdateKeycloakToken } from '@/services/keycloak-service'

// Axios by default has the timeout set to 0, which means no timeout.
// However, we want requests to timeout. Thus, set an explicit timeout.
// const httpClient = axios.create({
//     timeout: 3000
// })
/**
 * Before any request, we will check the lifetime of the Keycloak token.
 * If it's already expired or expiring soon (within the next 10 seconds),
 * we will force a token update before sending the request.
 */
axios.interceptors.request.use(
    async config => {
        console.log('AXIOS Interceptor', config)

        if (window.keycloak) {
            let tokenExpires = Math.round(
                window.keycloak.tokenParsed.exp +
                    window.keycloak.timeSkew -
                    new Date().getTime() / 1000
            )
            if (tokenExpires < 10) {
                console.log(
                    '[axios] Token expires soon. Updating token before sending request'
                )
                await forceUpdateKeycloakToken()
            } else {
                console.log(
                    '[axios] Token still valid. No need to refresh before sending request'
                )
            }
            config.headers.Authorization = `Bearer ${window.keycloak.token}`
        }
        return config
    },
    error => {
        return Promise.reject(error)
    }
)

/**
 * Gibt die Personendaten des angemeldeten Benutzers zurück.
 *
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const searchSmartSignup = searchQuery => {
    const smartSignupBaseUrl = `${import.meta.env.VITE_SMART_SIGNUP_API_BASEURL}`
    return axios.get(`${smartSignupBaseUrl}/${searchQuery}*`)
}

/**
 * Registriert einen Benutzer.
 *
 * @param {RegisterPerson} payload - Benutzerdatensatz, der registriert werden soll
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const registerPerson = payload =>
    axios.post('/api/user-management/persons/register', payload)

/**
 * Bestätigt den Opt-In eines Benutzers und die dabei angegebene E-Mail-Adresse
 * anhand des Opt-In-Tokens aus der Opt-In-Bestätigungs-E-Mail.
 * (Sowohl bei Neuanlage als auch bei Änderung der E-Mail-Adresse).
 *
 * @param {string} token - Token zur Bestätigung der E-Mail-Adresse
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const confirmMailAddress = token =>
    axios.post('/api/user-management/persons/confirm-mail-address', {
        confirmationToken: token
    })

/**
 * Benutzer-löschen-Prozess, Schritt 1:
 *
 * Fordert die Löschung des angemeldeten Benutzer an.
 * Es wird eine E-Mail mit einem Opt-Out-Token zur Bestätigung der Löschung an die E-Mail-Adresse des Benutzers versendet.
 * Unter Angabe des OptOut-Tokens kann der Benutzer sein Benutzerkonto endgültig löschen (Schritt 2: POST /persons/confirm-delete-account).
 *
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const requestDeletion = () =>
    axios.post('/api/user-management/persons/me/request-deletion')

/**
 * Benutzer-löschen-Prozess, Schritt 2:
 *
 * Bestätigt die Löschung eines Benutzers anhand des Opt-Out-Tokens aus der Opt-Out-Bestätigungs-E-Mail.
 *
 * @param {string} token - Token zur Bestätigung der Löschung des Benutzers
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const confirmDeletion = token =>
    axios.post('/api/user-management/persons/confirm-deletion', {
        optOutToken: token
    })

/**
 * Fordert den erneuten Versand einer Opt-In Bestätigungs-E-Mail
 * für unbestätigte Registrierungen an.
 *
 * @param {string} email - E-Mail-Adresse des Benutzers
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const resendOptInMail = email =>
    axios.post('/api/user-management/persons/resend-opt-in-mail', {
        email: email
    })

/**
 * Liefert Informationen zum Web Target, die z.B. zur Weiterleitung eines Benutzers nach der
 * Registrierungsbestätigung (OptIn) und für das Tracking der Benutzer-Journey genutzt werden können.
 *
 * @param {string} key - Schlüssel des Web Targets
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const getWebTarget = key =>
    axios.get(`/api/user-management/web-targets/${key}`)

/**
 * Gibt die Personendaten des angemeldeten Benutzers zurück.
 *
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const getPersonMe = () => axios.get('/api/user-management/persons/me')

/**
 * Aktualisiert die angegebenen Personendaten des angemeldeten Benutzers mit den angegebenen Werten.
 *
 * @param {Object} payload - Ein JSON Patch Objekt mit den Änderungsanweisungen für den Benutzerdatensatz des eingeloggten Benutzers
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const patchPersonMe = payload =>
    axios.patch('/api/user-management/persons/me', payload, {
        headers: {
            'Content-Type': 'application/json-patch+json'
        }
    })

/**
 * Ruft die E-Mail-Ändern Funktion für den angemeldeten Benutzer mit der angegebenen
 * neuen E-Mail-Adresse auf.
 *
 * Es wird eine Bestätigungs-E-Mail mit einem Opt-in Token an die neue E-Mail-Adresse versendet.
 *
 * Unter Angabe des Opt-in Tokens kann der Benutzer der Nutzung der neuen E-Mail-Adresse zustimmen
 * (Double Opt-in, Schritt 2: POST /persons/confirm-mail-address).
 *
 * @param {Object} payload - The request payload defining the email change
 * @param {string} payload.newEmail - The user's new email address
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const setPersonMeNewEmail = payload =>
    axios.post('/api/user-management/persons/me/change-mail-address', payload)

/**
 * Fordert den erneuten Versand einer Bestätigungs-E-Mail mit einem Opt-in Token
 * für den angemeldeten Benutzer an.
 *
 * Der E-Mail-Ändern-Prozess muss für den Benutzer aktiv sein und der Benutzer darf noch nicht
 * der Verwendung der neuen E-Mail-Adresse mittels Opt-in Token zugestimmt haben.
 *
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const resendSetPersonMeNewEmail = () =>
    axios.post('/api/user-management/persons/me/resend-opt-in-mail')

/**
 * Ändert das Passwort des angemeldeten Benutzers.
 *
 * Der Benutzer muss dafür sein altes Passwort und ein neues Passwort gemäß Passwortrichtlinie angeben.
 *
 * @param {Object} payload - The request payload defining the password change
 * @param {string} payload.newPassword - The user's new password
 * @param {string} payload.oldPassword - The user's current password
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const setPersonMeNewPassword = payload =>
    axios.post('/api/user-management/persons/me/change-password', payload)

/**
 * Registriert eine Mitgliedskennung für den angemeldeten Benutzer.
 * Der Benutzer darf noch keine Mitgliedskennung für sein Benutzerkonto registriert haben.
 * Die angegebene Mitgliedskennung muss für Meine Creditreform freigeschaltet und darf
 * noch keinem Benutzerkonto zugeordnet sein.
 *
 * @param {RegisterMembership} payload - Datensatz zur Registrierung einer Mitgliedskennung für den angemeldeten Benutzer
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const registerMembership = payload =>
    axios.post(
        '/api/user-management/persons/me/register-mitgliedskennung',
        payload
    )

/**
 * Passwort-Vergessen-Prozess, Schritt 1:
 *
 * Ruft die Passwort-Vergessen Funktion für die angegebene E-Mail-Adresse auf.
 * Es wird eine E-Mail mit einem Reset-Token zum Zurücksetzen des Passworts an die E-Mail-Adresse versendet.
 * Unter Angabe des Reset-Tokens kann der Benutzer ein neues Passwort setzen (Schritt 2: POST /persons/reset-password).
 *
 * @param {Object} payload - The request payload defining the password change
 * @param {string} payload.email - The user's email address
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const requestPasswordResetMail = payload =>
    axios.post(
        '/api/user-management/persons/request-password-reset-mail',
        payload
    )

/**
 * Passwort-Vergessen-Prozess, Schritt 2:
 *
 * Setzt ein neues Passwort für einen Benutzer.
 * Für das Setzen eines neuen Passworts muss das Reset-Token aus der Passwort-Zurücksetzen-E-Mail angegeben werden.
 * Eine Passwort-Zurücksetzen-E-Mail wird beim Aufruf der Passwort-Vergessen Funktion (Schritt 1: POST /persons/send-password-reset-mail) versendet.
 *
 * @param {Object} payload - The request payload defining the password change
 * @param {string} payload.newPassword - The new password
 * @param {string} payload.resetToken - The password reset token, received via email
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const resetPassword = payload =>
    axios.post('/api/user-management/persons/reset-password', payload)

/**
 * TypeDef RegisterPerson
 *
 * @typedef {Object} RegisterPerson
 * @property {(string|null)} email
 * @property {(string|null)} givenName
 * @property {(string|null)} surname
 * @property {(string|null)} password
 * @property {(string|null)} salutation - MR, MS
 * @property {(string|null)} [postRegistrationTargetKey]
 * @property {(string|null)} [postRegistrationToken]
 * @property {(string|null)} [consentMarketing] - GIVEN, REFUSED, NOT_EXPRESSED
 * @property {(string|null)} [consentRecherche] - GIVEN, REFUSED, NOT_EXPRESSED
 * @property {(string|null)} [crefonummer]
 * @property {(Object|null)} [ctoZugangsdaten]
 */

/**
 * TypeDef RegisterMembership
 *
 * @typedef {Object} RegisterMembership
 * @property {Object} ctoZugangsdaten
 */

/**
 * Fragt zum angemeldeten Benutzer den Status der Zwei Faktor Authentifizierung ab.
 *
 * @returns {Promise<AxiosResponse<T>>} - Axios promise object
 */
export const getPersonMFACredentials = () =>
    axios.get('/api/user-management/persons/me/credentials')
